'use client';

import type { Position } from '@/app/models/position';
import { useSearchPositionsQuery } from '@/app/services/positionApi';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import LateralDrawer from '@/components/common/LateralDrawer';
import PageTitle from '@/components/common/pageTitle';
import TextButton from '@/components/common/TextButton';
import NewPositionRestrictions from '@/components/PositionRestriction/NewPositionRestriction';
import PositionRestrictionTable from '@/components/PositionRestriction/PositionRestrictionTable';
import { Box, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

function PositionRestriction() {
  const theme = useTheme();
  const { t } = useTranslation(['positionRestriction', 'common']);
  const [drawerStatus, setDrawerStatus] = useState(false);

  const [position, setPosition] = useState<Position | null>(null);
  const [positionSearch, setPositionSearch] = useState('');
  const { data: positions, isFetching: isLoadingPositions } = useSearchPositionsQuery(
    {
      searchTerm: positionSearch,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    { skip: positionSearch.length < 1 },
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        mr: 2,
      }}
    >
      <PageTitle
        title={t('position.restriction')}
        titleLabel={` (${t('common:collaborator')})`}
        subtitle={`${t('position.restriction.subtitle')}`}
      />

      <Typography component="p" variant="fieldHeader">
        {t('common:position')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          mb: 2,
          alignItems: 'center',
        }}
      >
        <GenericAutoComplete
          value={position}
          onChange={(value) => {
            setPosition(value);
          }}
          equalityTest={(option, value) => option?.id === value?.id}
          getOptionLabel={(option) => `${option?.name}`}
          onSearch={(value) => {
            setPositionSearch(value);
          }}
          options={positionSearch !== '' && positions?.content ? positions?.content : []}
          placeholder={t('search.position') || ''}
          loading={isLoadingPositions}
          sx={{ width: theme.spacing(50) }}
        />

        <TextButton
          sx={{ ml: 'auto' }}
          text={`+ ${t('new.restriction')}`}
          onClick={() => {
            setDrawerStatus(true);
          }}
        />
      </Box>

      <PositionRestrictionTable searchTerm={position?.name || ''} />

      <LateralDrawer open={drawerStatus} setOpen={setDrawerStatus} position="right" size="bigger">
        <NewPositionRestrictions setDrawerOpen={setDrawerStatus} />
      </LateralDrawer>
    </Box>
  );
}

export default PositionRestriction;
